import { KalturaMediaImage } from '@kaltura-ott/ott-client-types';
import {
  KuxFilteredCategoriesDTO,
  PREDEFIENED_REFERENCES,
  UIDAPresentationTypeEnum,
  getResizerUrl,
  getWidthByHeight,
  toKuxImagesFromCategory,
} from '@kaltura-ott/tvpil';
import { container } from 'tsyringe';

import { assertPresence } from '../../../utils/error-handling';
import type { ViewableEntitiesType } from '../../entities';
import { SearchStore } from '../../search/search-store';
import { UserStore } from '../../user/user-store';
import { GridStore } from '../aside';
import { CategoryChildren } from '../category-children';
import { CategoryStore } from '../category-store';
import { DetailsStore } from '../details';
import { AssetDetailsPageStore } from '../details/asset-details-page-store';
import { LoginStore } from '../login';
import { getAsideFolder, getChildren } from '../page/page-store';
import { ApplicationsStore } from '../page/page-store';
import { PlayerStore } from '../player';
import { ZapperStore } from '../zapper';

/**
 * Root category store
 * @category Store
 */
export class RootStore extends CategoryStore<CategoryChildren> {
  zapper?: ZapperStore;
  login: LoginStore;
  applications?: ApplicationsStore;
  assetDetailsPages: AssetDetailsPageStore;
  grid?: GridStore;
  detailsStore: DetailsStore;
  recommendationFields: string[];
  search?: SearchStore;
  seriesRecordingPageTree?: KuxFilteredCategoriesDTO;
  playerStore?: PlayerStore;

  constructor(tree: KuxFilteredCategoriesDTO) {
    super(tree);
    const userStore = container.resolve<UserStore>('userStore');
    const asideFolder = getAsideFolder(tree, userStore.customerType);
    this.children = getChildren(tree, userStore.customerType) as CategoryChildren[];
    this.zapper = asideFolder.zapper;
    this.recommendationFields = tree.recommendationFields;
    this.login = assertPresence(asideFolder.login);
    this.applications = asideFolder.applications;
    this.assetDetailsPages = assertPresence(asideFolder.assetDetailsPages);
    this.grid = asideFolder.grid;
    this.detailsStore = container.resolve(DetailsStore);
    this.search = asideFolder.search;
    this.seriesRecordingPageTree = asideFolder.seriesRecordingPageTree;
    this.playerStore = asideFolder.player;
  }

  getDetailsPoster(asset: ViewableEntitiesType, options: { landscapeHeight?: number; portraitHeight?: number }) {
    const properties = this.assetDetailsPages.getImageProperties(asset.data.mediaType);

    if (!properties) {
      return;
    }

    const image = asset.data.images[properties.imageType];

    if (!image) {
      return undefined;
    }

    const [horizontal, vertical] = properties.imageRatio.split(/:|x/);

    const isLandscape = Number(horizontal) > Number(vertical);

    const targetHeight = isLandscape ? options.landscapeHeight : options.portraitHeight;

    const imageHeight = targetHeight ?? properties.imageHeight;

    const imageWidth = getWidthByHeight(imageHeight, properties.imageRatio);

    return {
      imageHeight,
      imageWidth,
      url: getResizerUrl(image.url, imageHeight, imageWidth),
    };
  }

  getDownloadMobileAppConfiguration() {
    const deeplinkTree = this.categoryTree.children.find(
      (category) => category.subtype === UIDAPresentationTypeEnum.deeplink,
    );

    if (!deeplinkTree) {
      return undefined;
    }

    return {
      branding: deeplinkTree.uiDesignElements,
      images: toKuxImagesFromCategory(deeplinkTree.images as unknown as KalturaMediaImage[]),
      ...deeplinkTree.deeplink,
    };
  }

  getHomePageHeroRailReferenceId() {
    const homePageStore = this.children.find((page) => page.referenceId === PREDEFIENED_REFERENCES.home);

    return homePageStore?.children.find(({ subtype }) => subtype === UIDAPresentationTypeEnum.hero)?.referenceId;
  }

  getEpgReferenceId() {
    return this.children.find((store) => store.subtype === UIDAPresentationTypeEnum.epg)?.referenceId;
  }
}
